import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

const KINDS = [
  { value: 'crew', label: 'Crew', emoji: '🤝', hint: '1 crew per user' },
  { value: 'group', label: 'Group', emoji: '👥', hint: 'Unlimited' },
  { value: 'club', label: 'Club', emoji: '🎵', hint: '1 club per artist' },
];

const GENRES = ['Hip Hop', 'Rap', 'Trap', 'R&B', 'Pop', 'Afrobeats', 'Dancehall', 'Reggae', 'Drill', 'Alternative', 'Other'];

const CreateCrewDialog = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState('crew');
  const [genre, setGenre] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingSummary(true);
      try {
        const r = await api.get('/users/me/community-summary');
        if (!cancelled) setSummary(r.data);
      } catch (e) {
        // ignore — dialog still usable; backend will enforce
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Auto-switch away from restricted kinds
  useEffect(() => {
    if (!summary) return;
    if (kind === 'crew' && summary.has_crew) setKind('group');
    if (kind === 'club' && summary.has_club) setKind('group');
  }, [summary]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const reset = () => {
    setName(''); setDescription(''); setKind('crew'); setGenre(''); setVisibility('public');
  };

  const isKindDisabled = (k) => {
    if (!summary) return false;
    if (k === 'crew' && summary.has_crew) return true;
    if (k === 'club' && summary.has_club) return true;
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    if (isKindDisabled(kind)) {
      toast.error(`You already have a ${kind}. Edit or leave it first.`);
      return;
    }
    setSubmitting(true);
    try {
      // Clubs use the new /api/clubs collection
      if (kind === 'club') {
        const r = await api.post('/clubs', {
          name: name.trim(),
          description: description.trim(),
          visibility,
        });
        reset();
        onCreated && onCreated({ ...r.data, kind: 'club' });
      } else {
        const r = await api.post('/crews', {
          name: name.trim(),
          description: description.trim(),
          kind,
          genre: genre || null,
          visibility,
        });
        reset();
        onCreated && onCreated(r.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="create-crew-dialog"
    >
      <div
        className="w-full max-w-lg bg-[#161620] border border-purple-500/40 rounded-xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <span>🤝</span> Start a Crew
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl"
            data-testid="close-create-crew"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Phase 6 Part 2: Community rules banner */}
          <div
            className="rounded-lg border border-purple-500/30 bg-purple-500/10 p-3 text-xs text-purple-100 leading-relaxed"
            data-testid="community-rules-banner"
          >
            <div className="font-semibold mb-1">Community rules</div>
            <ul className="list-disc pl-4 space-y-0.5 text-purple-200/90">
              <li><strong>Crew:</strong> exclusive — you can create <em>or</em> belong to 1 crew.</li>
              <li><strong>Group:</strong> open — join as many as you like.</li>
              <li><strong>Club:</strong> artist fanclub — 1 per artist (monetization-ready).</li>
            </ul>
            {loadingSummary && (
              <div className="mt-2 text-purple-300/80">Checking your current memberships…</div>
            )}
            {!loadingSummary && summary?.has_crew && (
              <div className="mt-2 text-purple-200">
                You're already in the crew <strong>{summary.crew?.name}</strong>.
              </div>
            )}
            {!loadingSummary && summary?.has_club && (
              <div className="mt-1 text-purple-200">
                You already own the club <strong>{summary.club?.name}</strong>.
              </div>
            )}
          </div>

          <div>
            <Label className="text-white mb-1 block">Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Late Night Producers"
              maxLength={80}
              required
              className="bg-black/40 text-white border-gray-700"
              data-testid="crew-name-input"
            />
          </div>

          <div>
            <Label className="text-white mb-1 block">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this crew about? Who is it for?"
              rows={3}
              maxLength={2000}
              className="bg-black/40 text-white border-gray-700"
              data-testid="crew-description-input"
            />
          </div>

          <div>
            <Label className="text-white mb-2 block">Type</Label>
            <div className="flex gap-2">
              {KINDS.map((k) => {
                const disabled = isKindDisabled(k.value);
                return (
                  <button
                    key={k.value}
                    type="button"
                    onClick={() => !disabled && setKind(k.value)}
                    disabled={disabled}
                    title={disabled ? `You already have a ${k.value}` : k.hint}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      disabled
                        ? 'bg-black/40 border-gray-800 text-gray-600 cursor-not-allowed'
                        : kind === k.value
                        ? 'bg-purple-600/30 border-purple-500 text-white'
                        : 'bg-black/40 border-gray-700 text-gray-300 hover:text-white'
                    }`}
                    data-testid={`crew-kind-${k.value}`}
                  >
                    {k.emoji} {k.label}
                    <div className="text-[10px] font-normal opacity-70 mt-0.5">{k.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white mb-1 block">Genre</Label>
              <select
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700"
                data-testid="crew-genre-select"
              >
                <option value="">Any genre</option>
                {GENRES.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-white mb-1 block">Visibility</Label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700"
                data-testid="crew-visibility-select"
              >
                <option value="public">🌐 Public</option>
                <option value="private">🔒 Private — request to join</option>
                <option value="invite_only">🤫 Invite-only — hidden from search</option>
              </select>
              <p className="text-[11px] text-gray-500 mt-1">
                {visibility === 'public' && 'Anyone can find and join in one click.'}
                {visibility === 'private' && 'Listed in discovery with a slim preview. People must request to join; admins approve.'}
                {visibility === 'invite_only' && 'Hidden from discovery entirely. Joinable only via direct invite from a crew admin.'}
              </p>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              disabled={submitting || isKindDisabled(kind)}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold disabled:opacity-60"
              data-testid="submit-create-crew"
            >
              {submitting ? 'Creating…' : `Create ${kind.charAt(0).toUpperCase() + kind.slice(1)}`}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="text-white border-gray-700"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCrewDialog;
